import React, { useEffect, useMemo, useState } from "react";
import {
  Grid,
  Typography,
  Alert,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Box,
  Stack,
  FormControlLabel,
  RadioGroup,
  Radio,
  IconButton,
} from "@mui/material";
import {
  Edit,
  Delete,
  KeyboardArrowRight,
  KeyboardArrowDown,
  Description,
  Close,
} from "@mui/icons-material";
import { customAPI } from "services/utils/CustomAPI";
import { showToast } from "services/utils/Status";
import { useSelector } from "react-redux";
import { getContrastingTextColor } from "services/utils/colorChecker";
import { usePayrollCycleRun } from "../../PayrollCycleRunContext";
import { themes } from "services/constants";
import { stickyHeader } from "services/constants";
import { Loader } from "components/Loader";
import { ConfirmationDialog } from "components/CustomComponents/ConfirmationDialog";
import { useForm } from "react-hook-form";
import { CustomTextBox } from "components/CustomComponents";
import * as Mui from "@mui/material";
import { TableEmptyState } from "components/CustomComponents/EmptyState";
import { tooltipStyleProps } from "services/constants/Styles";

// Sub-components
const DataTable = ({
  data,
  columns,
  emptyMessage = "No data available",
  renderRow,
  summaryRow,
}) => (
  <TableContainer sx={{ mt: 1, backgroundColor: themes?.whiteColor }}>
    <Table size="small" className="border">
      <TableHead sx={stickyHeader}>
        <TableRow>
          {columns.map((column, index) => (
            <TableCell key={index}>{column}</TableCell>
          ))}
        </TableRow>
      </TableHead>
      {!data || data.length === 0 ? (
        <TableBody>
          <TableRow>
            <TableCell colSpan={columns.length} align="center">
              <Alert severity="info">{emptyMessage}</Alert>
            </TableCell>
          </TableRow>
        </TableBody>
      ) : (
        <TableBody>
          {data.map((item, index) => renderRow(item, index))}
          {summaryRow && summaryRow()}
        </TableBody>
      )}
    </Table>
  </TableContainer>
);

const formatValue = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  return value;
};

const getTimeMinutes = (value) => {
  if (typeof value !== "string") return 0;

  const timeValue = value.trim();
  const match = timeValue.match(/^([+-])?\s*(\d+):(\d{2})$/);

  if (!match) return 0;

  const [, signChar, hours, minutes] = match;
  const sign = signChar === "-" ? -1 : 1;

  return sign * (Number(hours) * 60 + Number(minutes));
};

const formatTimeMinutes = (value) => {
  if (!Number.isFinite(value)) return "00:00";

  const sign = value < 0 ? "-" : "";
  const absoluteValue = Math.abs(value);
  const hours = Math.floor(absoluteValue / 60);
  const minutes = absoluteValue % 60;

  return `${sign}${String(hours).padStart(2, "0")}:${String(minutes).padStart(
    2,
    "0",
  )}`;
};

const OriginalOtEarningsTable = ({
  data,
  emptyMessage = "No OT earnings available",
}) => {
  const [collapsedBands, setCollapsedBands] = useState(() => new Set());

  const toggleBand = (index) => {
    setCollapsedBands((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  const visibleData = (data || []).filter(
    (group) => group.label !== "Attendance",
  );

  const totalMinutes = visibleData.reduce(
    (total, group) => total + getTimeMinutes(group.value),
    0,
  );

  return (
    <Box
      sx={{
        mt: 1,
        border: "1px solid #c7d7f5",
        borderRadius: 2,
        overflow: "hidden",
        backgroundColor: themes?.whiteColor,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 1,
          px: 2,
          py: 1.5,
          backgroundColor: "#eef3ff",
          borderBottom: "1px solid #c7d7f5",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box
            sx={{
              width: 22,
              height: 22,
              borderRadius: "50%",
              backgroundColor: "#2563eb",
              color: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 12,
              fontWeight: 700,
              flexShrink: 0,
            }}
          >
            1
          </Box>
          <Typography sx={{ fontWeight: 700, color: "#1d3a8a" }}>
            Original OT Earnings
          </Typography>
        </Box>
        <Typography variant="caption" color="text.secondary">
          Breakdown of original OT hours before any penalty or adjustment.
        </Typography>
      </Box>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>OT Component</TableCell>
            <TableCell>Source / Day Type</TableCell>
            <TableCell align="right">Worked Hours</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {!visibleData || visibleData.length === 0 ? (
            <TableRow>
              <TableCell colSpan={3} align="center">
                <Alert severity="info">{emptyMessage}</Alert>
              </TableCell>
            </TableRow>
          ) : (
            <>
              {visibleData.map((group, index) => {
                const isExpanded = !collapsedBands.has(index);
                const detailRows = (group.details || []).filter(
                  (detail) => detail.label !== "-",
                );

                return (
                  <React.Fragment key={`${group.label}-${index}`}>
                    <TableRow
                      onClick={() => toggleBand(index)}
                      sx={{ cursor: "pointer" }}
                    >
                      <TableCell sx={{ fontWeight: 700, color: "#1e293b" }}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                          }}
                        >
                          {isExpanded ? (
                            <KeyboardArrowDown
                              sx={{ fontSize: 18, color: "#64748b" }}
                            />
                          ) : (
                            <KeyboardArrowRight
                              sx={{ fontSize: 18, color: "#64748b" }}
                            />
                          )}
                          {formatValue(group.label)}
                        </Box>
                      </TableCell>
                      <TableCell />
                      <TableCell align="right" sx={{ fontWeight: 700 }}>
                        {formatValue(group.value)}
                      </TableCell>
                    </TableRow>

                    {isExpanded &&
                      detailRows.map((detail, detailIndex) => (
                        <TableRow
                          key={`${group.label}-${detail.label}-${detailIndex}`}
                        >
                          <TableCell />
                          <TableCell sx={{ color: "#475569" }}>
                            {formatSourceType(detail.label)}
                          </TableCell>
                          <TableCell align="right">
                            {formatValue(detail.value)}
                          </TableCell>
                        </TableRow>
                      ))}

                    {isExpanded && (
                      <TableRow>
                        <TableCell />
                        <TableCell>
                          <Typography
                            sx={{ fontWeight: 700, color: "#1d3a8a" }}
                          >
                            Total – {formatValue(group.label)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography
                            sx={{ fontWeight: 700, color: "#1d3a8a" }}
                          >
                            {formatValue(group.value)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })}
              <TableRow>
                <TableCell colSpan={2}>
                  <Typography sx={{ fontWeight: 700, color: "#1d3a8a" }}>
                    Total OT (Before Penalty)
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography sx={{ fontWeight: 700, color: "#1d3a8a" }}>
                    {formatTimeMinutes(totalMinutes)}
                  </Typography>
                </TableCell>
              </TableRow>
            </>
          )}
        </TableBody>
      </Table>
    </Box>
  );
};


const formatSignedMinutes = (minutes) => {
  if (!minutes) return { text: "—", color: "#64748b" };
  const formatted = formatTimeMinutes(Math.abs(minutes));
  return minutes > 0
    ? { text: `+${formatted}`, color: "#16a34a" }
    : { text: `-${formatted}`, color: "#dc2626" };
};

const formatSignedAdjustment = (value) =>
  formatSignedMinutes(getTimeMinutes(value));

const FinalOtCalculationTable = ({
  data,
  bandDeductions = [],
  emptyMessage = "No net earnings by band available",
}) => {
  const [expandedBand, setExpandedBand] = useState(null);

  const visibleData = useMemo(
    () => (data || []).filter((item) => item.bandName !== "Attendance"),
    [data],
  );

  const totals = useMemo(
    () =>
      visibleData.reduce(
        (total, item) => ({
          earnings: total.earnings + getTimeMinutes(item.earnings),
          deductions: total.deductions + getTimeMinutes(item.deductions),
          net: total.net + getTimeMinutes(item.net),
        }),
        { earnings: 0, deductions: 0, net: 0 },
      ),
    [visibleData],
  );

  return (
    <Box
      sx={{
        mt: 2,
        border: "1px solid #c7d7f5",
        borderRadius: 2,
        overflow: "hidden",
        backgroundColor: themes?.whiteColor,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 1,
          px: 2,
          py: 1.5,
          backgroundColor: "#eef3ff",
          borderBottom: "1px solid #c7d7f5",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box
            sx={{
              width: 22,
              height: 22,
              borderRadius: "50%",
              backgroundColor: "#16a34a",
              color: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 12,
              fontWeight: 700,
              flexShrink: 0,
            }}
          >
            3
          </Box>
          <Typography sx={{ fontWeight: 700, color: "#1d3a8a" }}>
            Final OT Calculation
          </Typography>
        </Box>
        <Typography variant="caption" color="text.secondary">
          Final OT hours after applying penalties and adjustments.
        </Typography>
      </Box>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>OT Component</TableCell>
            <TableCell align="right">Original (Hours)</TableCell>
            <TableCell align="right">Adjustment (Hours)</TableCell>
            <TableCell align="right">Final (Hours)</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {!visibleData || visibleData.length === 0 ? (
            <TableRow>
              <TableCell colSpan={4} align="center">
                <Alert severity="info">{emptyMessage}</Alert>
              </TableCell>
            </TableRow>
          ) : (
            <>
              {visibleData.map((item, index) => {
                const adjustment = formatSignedAdjustment(item.deductions);
                const isExpanded = expandedBand === index;
                const bandBreakdown = bandDeductions.find(
                  (band) => band.label === item.bandName,
                );
                const itemizedRows = (bandBreakdown?.details || []).filter(
                  (detail) => !detail.isTotal,
                );
                const creditRows = itemizedRows.filter(
                  (detail) => detail.isCredit,
                );
                const breakdownRows = itemizedRows.filter(
                  (detail) => !detail.isCredit,
                );
                const hasBreakdown = itemizedRows.length > 0;

                return (
                  <React.Fragment key={`${item.bandName}-${index}`}>
                    <TableRow
                      onClick={
                        hasBreakdown
                          ? () => setExpandedBand(isExpanded ? null : index)
                          : undefined
                      }
                      sx={{
                        cursor: hasBreakdown ? "pointer" : "default",
                      }}
                    >
                      <TableCell sx={{ color: "#1e293b" }}>
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                        >
                          {hasBreakdown ? (
                            isExpanded ? (
                              <KeyboardArrowDown
                                sx={{ fontSize: 18, color: "#64748b" }}
                              />
                            ) : (
                              <KeyboardArrowRight
                                sx={{ fontSize: 18, color: "#64748b" }}
                              />
                            )
                          ) : (
                            <KeyboardArrowRight
                              sx={{ fontSize: 18, color: "#cbd5e1" }}
                            />
                          )}
                          {formatValue(item.bandName)}
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        {formatValue(item.earnings)}
                      </TableCell>
                      <TableCell align="right">{adjustment.text}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>
                        {formatValue(item.net)}
                      </TableCell>
                    </TableRow>

                    {isExpanded &&
                      breakdownRows.length === 0 &&
                      creditRows.length === 0 && (
                        <TableRow sx={{ backgroundColor: "#f8fafc" }}>
                          <TableCell colSpan={4} sx={{ borderBottom: "none" }}>
                            <Typography
                              variant="body2"
                              sx={{
                                color: "#94a3b8",
                                fontStyle: "italic",
                                pl: 3,
                              }}
                            >
                              No itemized breakdown available for this band.
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}

                    {isExpanded &&
                      creditRows.map((credit, creditIndex) => {
                        const creditMinutes = getTimeMinutes(credit.value);
                        return (
                          <TableRow
                            key={`${item.bandName}-${credit.label}-${creditIndex}-credit`}
                            sx={{ backgroundColor: "#f0fdf4" }}
                          >
                            <TableCell sx={{ pl: 5, borderBottom: "none" }}>
                              <Typography
                                component="span"
                                variant="body2"
                                sx={{ color: "#166534" }}
                              >
                                {formatValue(credit.label)}
                              </Typography>
                              {credit.earningType && (
                                <Typography
                                  component="span"
                                  variant="body2"
                                  sx={{ color: "#94a3b8" }}
                                >
                                  {" "}
                                  — {formatSourceType(credit.earningType)}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell sx={{ borderBottom: "none" }} />
                            <TableCell
                              align="right"
                              sx={{ borderBottom: "none" }}
                            >
                              <Typography
                                variant="body2"
                                sx={{ color: "#16a34a", fontWeight: 700 }}
                              >
                                +{formatTimeMinutes(creditMinutes)}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ borderBottom: "none" }} />
                          </TableRow>
                        );
                      })}

                    {isExpanded &&
                      breakdownRows.map((detail, detailIndex) => {
                        // `deducted_quantity` from the backend is an
                        // unsigned magnitude -- every penalty line
                        // item is a deduction, so render it as one
                        // rather than relying on sign detection.
                        const detailMinutes = getTimeMinutes(detail.value);
                        return (
                          <TableRow
                            key={`${item.bandName}-${detail.label}-${detailIndex}`}
                            sx={{ backgroundColor: "#f8fafc" }}
                          >
                            <TableCell sx={{ pl: 5, borderBottom: "none" }}>
                              <Typography
                                component="span"
                                variant="body2"
                                sx={{ color: "#475569" }}
                              >
                                {formatValue(detail.label)}
                              </Typography>
                              {detail.earningType && (
                                <Typography
                                  component="span"
                                  variant="body2"
                                  sx={{ color: "#94a3b8" }}
                                >
                                  {" "}
                                  — {formatSourceType(detail.earningType)}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell sx={{ borderBottom: "none" }} />
                            <TableCell align="right" sx={{ borderBottom: "none" }}>
                              <Typography variant="body2">
                                {detailMinutes
                                  ? `-${formatTimeMinutes(detailMinutes)}`
                                  : formatValue(detail.value)}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ borderBottom: "none" }} />
                          </TableRow>
                        );
                      })}

                    {isExpanded && (
                      <TableRow sx={{ backgroundColor: "#1d3a8a" }}>
                        <TableCell sx={{ borderBottom: "none" }}>
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 700, color: "#fff", pl: 3 }}
                          >
                            Total Adjustment ({formatValue(item.bandName)})
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ borderBottom: "none" }} />
                        <TableCell align="right" sx={{ borderBottom: "none" }}>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 700,
                              color: "#fff",
                            }}
                          >
                            {adjustment.text}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ borderBottom: "none" }} />
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })}
              <TableRow>
                <TableCell>
                  <Typography sx={{ fontWeight: 700, color: "#1d3a8a" }}>
                    Total OT (After Penalty)
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography sx={{ fontWeight: 700, color: "#1d3a8a" }}>
                    {formatTimeMinutes(totals.earnings)}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography sx={{ fontWeight: 700, color: "#1d3a8a" }}>
                    {formatSignedMinutes(totals.deductions).text}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography sx={{ fontWeight: 700, color: "#1d3a8a" }}>
                    {formatTimeMinutes(totals.net)}
                  </Typography>
                </TableCell>
              </TableRow>
            </>
          )}
        </TableBody>
      </Table>
    </Box>
  );
};

const normalizeNetEarningsByBand = (netEarningsByBand = {}) =>
  Object.entries(netEarningsByBand).map(([bandName, bandData]) => ({
    bandName,
    earnings: bandData?.earning,
    deductions: bandData?.adjustment,
    net: bandData?.net_earnings,
  }));

const normalizeEarningsByBand = (earningsByBand = {}) =>
  Object.entries(earningsByBand).map(([bandName, bandData]) => {
    const earnings = Array.isArray(bandData?.earnings) ? bandData.earnings : [];

    return {
      label: bandName,
      value: bandData?.total_hours,
      details: earnings.length
        ? earnings.map((earning) => ({
            label: earning?.type,
            value: earning?.hours,
          }))
        : [{ label: "-", value: "-" }],
    };
  });

const SOURCE_TYPE_LABELS = {
  OT: "Weekday Overtime",
  Overtime: "Weekday Overtime",
  Weekoff: "Week Off",
  WeekOff: "Week Off",
  Holiday: "Holiday",
  Attendance: "Attendance",
};

const formatSourceType = (type) => SOURCE_TYPE_LABELS[type] ?? type ?? "-";

const normalizeBandDeductions = (deductionsByBand = {}) =>
  Object.entries(deductionsByBand).map(([bandName, deductionData]) => {
    // The pay-run response provides each band's deducted quantities in
    // `penalties`; retain `actual_penalties` as a fallback for older responses.
    const penalties = Array.isArray(deductionData?.penalties)
      ? deductionData.penalties
      : Array.isArray(deductionData?.actual_penalties)
        ? deductionData.actual_penalties
        : [];
    const totalDeductedMinutes = penalties.reduce(
      (total, penalty) => total + getTimeMinutes(penalty?.deducted_quantity),
      0,
    );

    return {
      label: bandName,
      value: `Factor ${formatValue(deductionData?.factor)}`,
      details: [
        ...(penalties.length
          ? penalties.map((penalty) => ({
              label: formatValue(penalty?.penalty_type),
              earningType: penalty?.earning_type,
              value: penalty?.deducted_quantity,
            isCredit: /credit/i.test(penalty?.penalty_type ?? ""),
          }))
          : [{ label: "No deduction", value: "-" }]),
        {
          label: "Total deducted",
          value: formatTimeMinutes(totalDeductedMinutes),
          isTotal: true,
        },
      ],
    };
  });

const AttendanceCalendar = ({ attendanceDayDetails, colorCode }) => {
  const flatDays = useMemo(() => {
    if (!attendanceDayDetails) return [];
    return attendanceDayDetails.flat().filter((d) => d.day_number !== "");
  }, [attendanceDayDetails]);

  const statusLabelMap = useMemo(() => {
    if (!Array.isArray(colorCode)) return {};
    return colorCode.reduce((acc, item) => {
      acc[item.status] = item.label;
      return acc;
    }, {});
  }, [colorCode]);

  const DAY_NAMES = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

  if (!flatDays.length) return null;

  return (
    <>
      <Grid container sx={{ paddingTop: 1 }}>
        <SectionHeader>Attendance</SectionHeader>
      </Grid>
      <Mui.TableContainer sx={{ mt: 1, width: "100%", overflowX: "auto" }}>
        <Mui.Table size="small" className="border">
          <Mui.TableHead
            sx={{
              backgroundColor: themes.primary,
              position: "sticky",
              top: 0,
              zIndex: 10,
            }}
          >
            <Mui.TableRow>
              {flatDays.map((day, idx) => {
                const weekIdx = Math.floor(
                  attendanceDayDetails
                    .flat()
                    .findIndex((d) => d.day_number === day.day_number) % 7,
                );
                const dayName = DAY_NAMES[weekIdx] ?? "";
                return (
                  <Mui.TableCell
                    key={idx}
                    align="center"
                    sx={{ padding: "4px 2px", minWidth: 42, width: 42 }}
                  >
                    <Mui.Stack>
                      <Mui.Typography
                        textAlign="center"
                        sx={{
                          color: themes.headLine,
                          fontWeight: "bold",
                          fontSize: 11,
                        }}
                      >
                        {day.day_number}
                      </Mui.Typography>
                      <Mui.Typography
                        textAlign="center"
                        sx={{
                          color: themes.headLine,
                          fontWeight: "bold",
                          fontSize: 10,
                        }}
                      >
                        {dayName}
                      </Mui.Typography>
                    </Mui.Stack>
                  </Mui.TableCell>
                );
              })}
            </Mui.TableRow>
          </Mui.TableHead>
          <Mui.TableBody>
            <Mui.TableRow>
              {flatDays.map((day, idx) => {
                const tooltipTitle = day.legend
                  ? statusLabelMap[day.legend]
                    ? `${day.legend} — ${statusLabelMap[day.legend]}`
                    : day.legend
                  : "";
                return (
                  <Mui.TableCell
                    key={idx}
                    align="center"
                    sx={{ padding: "4px 2px", minWidth: 42, width: 42 }}
                  >
                    <Mui.Tooltip
                      arrow
                      placement="top"
                      slotProps={tooltipStyleProps}
                      title={tooltipTitle}
                    >
                      <Box
                        sx={{
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          width: 30,
                          height: 30,
                          backgroundColor: day.colour_code,
                          borderRadius: "4px",
                          flexShrink: 0,
                          cursor: "pointer",
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize:
                              day.legend?.length > 2
                                ? 8
                                : day.legend?.length === 2
                                  ? 11
                                  : 13,
                            fontWeight: 700,
                            lineHeight: 1,
                            color: getContrastingTextColor(
                              day.colour_code || "#181818",
                            ),
                            whiteSpace: "nowrap",
                            letterSpacing:
                              day.legend?.length > 2 ? "-0.5px" : "normal",
                          }}
                        >
                          {day.legend}
                        </Typography>
                      </Box>
                    </Mui.Tooltip>
                  </Mui.TableCell>
                );
              })}
            </Mui.TableRow>
          </Mui.TableBody>
        </Mui.Table>
      </Mui.TableContainer>
    </>
  );
};

const SectionHeader = ({ children }) => (
  <Typography
    sx={{
      fontSize: 16,
      fontWeight: "bold",
      color: "#556774",
      mt: 2,
    }}
  >
    {children}
  </Typography>
);

// Main Component
export const AttendanceDetails = ({
  setRecallEmployee,
  colorCode,
  attendanceDetails,
  loading,
  reCallApi,
}) => {
  const requestCompanyId = useSelector((state) => state.tokenReducer.topbarCompanyId);
  const { domain } = useSelector((state) => state.tokenReducer);
  const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);
  const {
    attendanceDayDetails,
    setAttendanceDayDetails,
    payScheduleId,
    userId,
    payScheduleData,
  } = usePayrollCycleRun();

  const {
    watch,
    register,
    setValue,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      reason: "",
      adjustmentType: "add shift",
      no_of_shift_adjust: "",
    },
  });

  const [isPenalty, setIsPenalty] = useState(false);
  const [isSummary, setIsSummary] = useState(false);
  const [isOtherEarnings, setIsOtherEarnings] = useState(false);
  const [selectedData, setSelectedData] = useState([]);
  const [expandedRows, setExpandedRows] = useState(false);
  const [showOtCalculationDialog, setShowOtCalculationDialog] =
    useState(false);

  const isEditable =
    payScheduleData?.status === "Submitted" ||
    payScheduleData?.status === "Approved" ||
    payScheduleData?.status === "Ready for Review";

  const summaryData = useMemo(
    () => attendanceDetails?.attendance?.summary?.[0] || {},
    [attendanceDetails],
  );

  const netEarningsByBand = useMemo(() => {
    const source =
      attendanceDetails?.earning_summary?.net_earnings_by_band ??
      attendanceDetails?.net_earnings_by_band ??
      {};

    return normalizeNetEarningsByBand(source);
  }, [attendanceDetails]);

  const earningsByBand = useMemo(() => {
    const source =
      attendanceDetails?.earning_summary?.earnings_by_band ??
      attendanceDetails?.earnings_by_band ??
      {};

    return normalizeEarningsByBand(source);
  }, [attendanceDetails]);

  const hasOtBands = earningsByBand.some((band) => band.label !== "Attendance");

  const isBandEarning = (type) =>
    typeof type === "string" &&
    earningsByBand.some(
      (band) => band.label && type.startsWith(band.label),
    );

  const bandDeductions = useMemo(() => {
    const source =
      attendanceDetails?.earning_summary?.deductions_by_band ??
      attendanceDetails?.deductions_by_band ??
      {};

    return normalizeBandDeductions(source);
  }, [attendanceDetails]);

  // ── Flatten + format attendance timeline ──────────────────────────────────
  const formatAttendanceData = (data) => {
    const flatData = data.flat();
    const result = [];
    let week = [];
    let dayCursor = 1;

    flatData.forEach((day) => {
      const date = new Date(day.date);
      let jsDay = date.getDay();
      jsDay = jsDay === 0 ? 7 : jsDay;

      while (dayCursor < jsDay) {
        week.push({
          legend: "-",
          day_number: "",
          colour_code: themes.lightShadow,
        });
        dayCursor++;
      }

      week.push({
        legend: day.legend,
        day_number: day.day_number,
        colour_code:
          day.colour_code === "#ffffff" ? "#272626ff" : day.colour_code,
      });
      dayCursor++;

      if (week.length === 7) {
        result.push(week);
        week = [];
        dayCursor = 1;
      }
    });

    while (week.length > 0 && week.length < 7) {
      week.push({
        legend: "-",
        day_number: "",
        colour_code: themes.lightShadow,
      });
    }

    if (week.length) result.push(week);
    return result;
  };

  useEffect(() => {
    if (!loading && attendanceDetails) {
      const timeline =
        attendanceDetails?.attendance?.attendance_timeline?.[0]?.attendance ||
        [];
      const formattedAttendance = formatAttendanceData(timeline);
      setAttendanceDayDetails(formattedAttendance);
    }
  }, [attendanceDetails, loading, setAttendanceDayDetails]);

  if (payScheduleData?.status === "Draft") {
    return (
      <Alert severity="info" fullWidth>
        Payroll run has not been initiated to the user yet.
      </Alert>
    );
  }

  const handleCancelClose = () => {
    setShowConfirmationDialog(false);
    setSelectedData([]);
    reset();
  };

  const handlePenalty = async () => {
    console.log(selectedData);
    const payload = {
      penalty_id: selectedData.id,
      pay_schedule_id: payScheduleId,
      user_id: selectedData.user_id,
      penalty_type: selectedData.penaltyTableType,
    };
    const response = await customAPI(
      `${domain}payroll/operations/payrollprocessing/payrollruncycle/remove_penalty/?company_id=${requestCompanyId}`,
      "PUT",
      payload,
    );
    if (response.ok) {
      showToast("success", "Penalty removed.");
      setShowConfirmationDialog(false);
      reCallApi(true);
      setRecallEmployee(true);
    }
    watch("reason");
  };

  const onSubmit = async (data) => {
    const payload = {
      user_id: userId,
      pay_schedule_id: payScheduleId,
      adjustment_type: watch("adjustmentType"),
      adjusted_value: watch("no_of_shift_adjust"),
      payrun_basis: attendanceDetails.calculation_based_on,
      reason: watch("reason"),
    };
    const response = await customAPI(
      `${domain}payroll/operations/payrollprocessing/payrollruncycle/update_manual_adjustment/?company_id=${requestCompanyId}`,
      "PUT",
      payload,
    );
    if (response.ok) {
      showToast("success", "Adjustment added.");
      setShowConfirmationDialog(false);
      reCallApi(true);
    }
  };

  const hanldeOtherEarnings = async () => {
    const payload = {
      pay_schedule_id: payScheduleId,
      earning_id: selectedData.id,
    };
    const response = await customAPI(
      `${domain}payroll/operations/payrollprocessing/payrollruncycle/remove_other_earnings/?company_id=${requestCompanyId}`,
      "PUT",
      payload,
    );
    if (response.ok) {
      showToast("success", "Other earning removed.");
      setShowConfirmationDialog(false);
      reCallApi(true);
    }
    setShowConfirmationDialog(false);
  };

  const handleReason = (event) => {
    const reason = event.target.value;
    setValue("reason", reason || "");
  };

  const handleAdjustShift = (event) => {
    const validPattern = /^[0-9]?(\.\d{0,2})?$/;
    if (validPattern.test(event.target.value)) {
      setValue("no_of_shift_adjust", event.target.value);
    } else {
      setValue("no_of_shift_adjust", "");
    }
  };

  const renderPenaltyDetails = ({
    showActualPenalties = true,
    showPenalties = true,
  } = {}) => {
    // `penalties` contains all calculated/configured penalties. The table must
    // display only the penalty that was actually applied to this pay run.
    const actualPenalties = Array.isArray(
      attendanceDetails?.attendance?.actual_penalties,
    )
      ? attendanceDetails.attendance.actual_penalties
      : [];
    const penalties = Array.isArray(attendanceDetails?.attendance?.penalties)
      ? attendanceDetails.attendance.penalties
      : [];

    const filteredPenalties = actualPenalties.filter((penalty) => {
      // Hide "Outside Penalty" when is_priority_deduction_applied is true
      if (
        attendanceDetails?.is_priority_deduction_applied &&
        penalty?.type === "Outside Penalty"
      ) {
        return false;
      }
      // Show "Outside Penalty" only when is_priority_deduction_applied is false
      if (
        !attendanceDetails?.is_priority_deduction_applied &&
        penalty?.type === "Outside Penalty"
      ) {
        return true;
      }
      // Show all other penalties
      return true;
    });

    return (
      <>
        {showActualPenalties && (
          <Box
            sx={{
              mt: 2,
              border: "1px solid #c7d7f5",
              borderRadius: 2,
              overflow: "hidden",
              backgroundColor: themes?.whiteColor,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: 1,
                px: 2,
                py: 1.5,
                backgroundColor: "#eef3ff",
                borderBottom: "1px solid #c7d7f5",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box
                  sx={{
                    width: 22,
                    height: 22,
                    borderRadius: "50%",
                    backgroundColor: "#7c3aed",
                    color: "#fff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 12,
                    fontWeight: 700,
                    flexShrink: 0,
                  }}
                >
                  2
                </Box>
                <Typography sx={{ fontWeight: 700, color: "#1d3a8a" }}>
                  Penalty Applied
                </Typography>
              </Box>
              <Typography variant="caption" color="text.secondary">
                List of penalties applied.
              </Typography>
            </Box>
            <Box sx={{ "& .MuiTableContainer-root": { mt: 0 } }}>
              <DataTable
                data={filteredPenalties}
                columns={[
                  "Penalty",
                  "Occurrences",
                  "Calculation Quantity",
                  "Penalty Quantity (Hours)",
                  "",
                ]}
                renderRow={(data, index) => (
                  <React.Fragment key={index}>
                    <TableRow>
                      <TableCell>{data?.type}</TableCell>
                      <TableCell>{data?.count}</TableCell>
                      <TableCell>{data?.quantity}</TableCell>
                      <TableCell>{data?.hours || "0"}</TableCell>
                      <TableCell>
                        <IconButton
                          disabled={isEditable}
                          onClick={() => {
                            setShowConfirmationDialog(true);
                            setSelectedData({
                              ...data,
                              penaltyTableType: "Actual Penalty",
                            });
                            setIsPenalty(true);
                            setIsOtherEarnings(false);
                            setIsSummary(false);
                          }}
                        >
                          <Delete
                            color={
                              isEditable ? themes.disabledColor : "error"
                            }
                            sx={{ fontSize: 20, cursor: "pointer" }}
                          />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                )}
                summaryRow={() => {
                  const totalAdjustedMinutes = filteredPenalties.reduce(
                    (sum, p) => sum + getTimeMinutes(p?.hours),
                    0,
                  );
                  return (
                    <TableRow>
                      <TableCell>
                        <strong>Total Penalty</strong>
                      </TableCell>
                      <TableCell />
                      <TableCell />
                      <TableCell>
                        <strong>
                          {formatTimeMinutes(totalAdjustedMinutes)} hours
                        </strong>
                      </TableCell>
                      <TableCell />
                    </TableRow>
                  );
                }}
              />
            </Box>
          </Box>
        )}

        {showPenalties && penalties.length > 0 && (
          <>
            <SectionHeader>Penalty</SectionHeader>
            <DataTable
              data={penalties}
              columns={[
                "Penalty",
                "Count",
                "Hours",
                "Basis",
                "Qty",
                "Factor",
                "Adjusted",
                "",
              ]}
              renderRow={(data, index) => (
                <TableRow key={index}>
                  <TableCell>{data?.type}</TableCell>
                  <TableCell>{data?.count}</TableCell>
                  <TableCell>{data?.hours || "0"}</TableCell>
                  <TableCell>{data?.based_on}</TableCell>
                  <TableCell>{data?.quantity}</TableCell>
                  <TableCell>{data?.factor}</TableCell>
                  <TableCell>{data?.adjusted}</TableCell>
                  <TableCell>
                    <IconButton
                      disabled={isEditable}
                      onClick={() => {
                        setShowConfirmationDialog(true);
                        setSelectedData({
                          ...data,
                          penaltyTableType: "Penalty",
                        });
                        setIsPenalty(true);
                        setIsOtherEarnings(false);
                        setIsSummary(false);
                      }}
                    >
                      <Delete
                        color={isEditable ? themes.disabledColor : "error"}
                        sx={{ fontSize: 20, cursor: "pointer" }}
                      />
                    </IconButton>
                  </TableCell>
                </TableRow>
              )}
              summaryRow={() => {
                const totalAdjusted = penalties.reduce(
                  (sum, p) => sum + (Number(p?.adjusted) || 0),
                  0,
                );
                return (
                  <TableRow>
                    <TableCell>
                      <strong>Penalty Shift</strong>
                    </TableCell>
                    <TableCell colSpan={5} />
                    <TableCell>
                      <strong>{totalAdjusted.toFixed(2)}</strong>
                    </TableCell>
                  </TableRow>
                );
              }}
            />
          </>
        )}
      </>
    );
  };

  return loading ? (
    <Loader />
  ) : (
    <Grid container sx={{ p: 1, backgroundColor: themes?.primary }}>
      <ConfirmationDialog
        open={showConfirmationDialog}
        onClose={handleCancelClose}
        onConfirm={() => {
          if (isPenalty) return handlePenalty();
          if (isSummary) return handleSubmit(onSubmit)();
          return hanldeOtherEarnings();
        }}
        title={isSummary ? "Adjustment" : "Confirm Deletion"}
        confirmText={isSummary ? "Add" : "Delete"}
        cancelText="Cancel"
        showChildren={true}
      >
        {(isPenalty || isOtherEarnings) && (
          <Box sx={{ mb: 1 }}>
            <Typography>
              {`Are you sure you want to exclude ${
                selectedData?.type || "this item"
              } from the current payroll.`}
            </Typography>
          </Box>
        )}
        {isSummary && (
          <>
            <Grid container spacing={1} sx={{ py: 1 }}>
              <Grid item xs={3.5}></Grid>
              <Grid item xs={4}>
                <Typography color="text.secondary" display="flex" gap={2}>
                  <span>Name</span>
                  <strong>{attendanceDetails?.employee_name ?? "-"}</strong>
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography color="text.secondary" display="flex" gap={2}>
                  <span>Payable Shift</span>
                  <strong>{summaryData.total_payable_shift ?? 0}</strong>
                </Typography>
              </Grid>
            </Grid>
            <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
              <Typography sx={{ width: 180, fontWeight: "bold" }}>
                Adjustment type
              </Typography>
              <Box sx={{ display: "flex", mt: 1 }}>
                <RadioGroup
                  row
                  onChange={(e) => setValue("adjustmentType", e.target.value)}
                  defaultValue={watch("adjustmentType")}
                >
                  <FormControlLabel
                    control={<Radio />}
                    label="Add Shift"
                    value="add shift"
                  />
                  <FormControlLabel
                    control={<Radio />}
                    label="Deduct Shift"
                    value="deduct shift"
                  />
                </RadioGroup>
              </Box>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
              <Typography sx={{ width: 250, fontWeight: "bold" }}>
                No of Shift to Adjust
              </Typography>
              <CustomTextBox
                rules={{
                  ...register("no_of_shift_adjust", {
                    required: "No of shift to adjust is required",
                  }),
                }}
                selectedValue={watch("no_of_shift_adjust")}
                updateDataFunction={handleAdjustShift}
                placeHolder="Enter adjustment shift"
                errorMessage={errors?.no_of_shift_adjust?.message || ""}
                textBoxError={!!errors?.no_of_shift_adjust}
              />
            </Box>
          </>
        )}
        <Box
          sx={{
            ...(isSummary && { display: "flex", alignItems: "center", mb: 1 }),
          }}
        >
          <Typography
            sx={{
              ...(isSummary
                ? { width: 250, fontWeight: "bold" }
                : { fontWeight: "bold", mb: 0.5 }),
            }}
          >
            Reason (Optional)
          </Typography>
          <CustomTextBox
            type="multiline"
            rules={{ ...register("reason") }}
            selectedValue={watch("reason")}
            updateDataFunction={handleReason}
            placeHolder={
              isSummary
                ? "Enter a reason for adjustment"
                : "Enter a reason for deletion"
            }
          />
        </Box>
      </ConfirmationDialog>

      <Mui.Drawer
        anchor="right"
        open={showOtCalculationDialog}
        onClose={() => setShowOtCalculationDialog(false)}
        sx={{
          "& .MuiDrawer-paper": {
            width: "680px",
            maxWidth: "90vw",
            backgroundColor: themes?.primary,
          },
        }}
      >
        <Box sx={{ p: 2 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 1,
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              OT Calculation
            </Typography>
            <Mui.IconButton
              onClick={() => setShowOtCalculationDialog(false)}
              size="small"
            >
              <Close fontSize="small" />
            </Mui.IconButton>
          </Box>

          <OriginalOtEarningsTable
            data={earningsByBand}
            emptyMessage="No OT earnings available"
          />

          {renderPenaltyDetails({ showPenalties: false })}

          {netEarningsByBand?.length > 0 && (
            <FinalOtCalculationTable
              data={netEarningsByBand}
              bandDeductions={bandDeductions}
              emptyMessage="No net earnings by band available"
            />
          )}
        </Box>
      </Mui.Drawer>

      {/* Summary Stats */}
      <Grid container spacing={1} sx={{ py: 1 }}>
        <Grid item xs={4}>
          <Typography variant="body2" color="text.secondary">
            Total Days <strong>{attendanceDetails?.total_days ?? "--"}</strong>
          </Typography>
        </Grid>
        <Grid item xs={4}>
          <Typography variant="body2" color="text.secondary">
            Present Days{" "}
            <strong>{attendanceDetails?.present_days ?? "--"}</strong>
          </Typography>
        </Grid>
        <Grid item xs={4}>
          <Typography variant="body2" color="text.secondary">
            Calculated Based On{" "}
            <strong>{attendanceDetails?.calculation_based_on ?? "--"}</strong>
          </Typography>
        </Grid>
      </Grid>

      <SectionHeader>Earnings</SectionHeader>
      <DataTable
        data={attendanceDetails?.attendance?.earnings}
        columns={[
          "Shifts",
          "Count",
          "Hours",
          "Basis",
          "Qty",
          "Factor",
          "Adjusted",
        ]}
        renderRow={(data, index) => (
          <React.Fragment key={index}>
            <TableRow>
              <TableCell>{data?.type}</TableCell>
              <TableCell>{data?.count ?? "0"}</TableCell>
              <TableCell>{data?.hours ?? "0"}</TableCell>
              <TableCell>{data?.based_on}</TableCell>
              <TableCell>{data?.quantity}</TableCell>
              <TableCell>{data?.factor}</TableCell>
              <TableCell>{data?.adjusted}</TableCell>
            </TableRow>
          </React.Fragment>
        )}
        summaryRow={() => (
          <TableRow>
            <TableCell>
              <strong>Base</strong>
            </TableCell>
            <TableCell colSpan={5} />
            <TableCell>
              <strong>{summaryData.base_shift ?? 0}</strong>
            </TableCell>
          </TableRow>
        )}
      />

      {renderPenaltyDetails({ showActualPenalties: false })}

      <SectionHeader>Summary</SectionHeader>
      <TableContainer sx={{ mt: 1, backgroundColor: themes?.whiteColor }}>
        <Table size="small" className="border">
          <TableBody>
            <TableRow>
              <TableCell>
                <strong>Base {attendanceDetails.calculation_based_on}</strong>
              </TableCell>
              <TableCell>{summaryData.base_shift ?? 0}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                <strong>
                  Penalty {attendanceDetails.calculation_based_on}
                </strong>
              </TableCell>
              <TableCell>{summaryData.penalty_shift ?? 0}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                <strong>Manual Adjustment (+/-)</strong>
              </TableCell>
              <TableCell>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <span>
                    {summaryData.adjustement_type === "add shift" ? "+" : "-"}
                    {summaryData.manual_adjusted ?? 0}
                  </span>
                  <IconButton
                    disabled={isEditable}
                    onClick={() => {
                      setShowConfirmationDialog(true);
                      setSelectedData(summaryData);
                      setValue("adjustmentType", summaryData?.adjustement_type);
                      setValue(
                        "no_of_shift_adjust",
                        parseInt(summaryData?.manual_adjusted),
                      );
                      setIsSummary(true);
                      setIsPenalty(false);
                      setIsOtherEarnings(false);
                    }}
                  >
                    <Edit
                      color={isEditable ? themes.disabledColor : "primary"}
                      sx={{ fontSize: 20, cursor: "pointer" }}
                    />
                  </IconButton>
                </Stack>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                <strong>
                  Total Payable {attendanceDetails.calculation_based_on}
                </strong>
              </TableCell>
              <TableCell>
                <strong>{summaryData.total_payable_shift ?? 0}</strong>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      <Grid
        container
        alignItems="flex-end"
        justifyContent="space-between"
        sx={{ mt: 3 }}
      >
        <Grid item>
          <SectionHeader>Other Earnings</SectionHeader>
        </Grid>
        <Grid item>
          {hasOtBands && (
            <Mui.Button
              variant="outlined"
              size="small"
              startIcon={<Description />}
              onClick={() => setShowOtCalculationDialog(true)}
              sx={{
                borderColor: "#00845a",
                color: "#006655",
                fontWeight: 700,
                textTransform: "none",
                whiteSpace: "nowrap",
                "&:hover": {
                  borderColor: "#006655",
                  backgroundColor: "#f2fbf8",
                },
              }}
            >
              View OT Calculation
            </Mui.Button>
          )}
        </Grid>
      </Grid>
      <DataTable
        data={attendanceDetails?.attendance?.other_earnings}
        columns={[
          "Earnings",
          "Count",
          "Hours",
          "Basis",
          "Qty",
          "Factor",
          "",
        ]}
        renderRow={(data, index) => (
          <TableRow key={index}>
            <TableCell sx={{ width: 150 }}>{data?.type}</TableCell>
            <TableCell>{data?.count}</TableCell>
            <TableCell>{data?.hours ?? "0"}</TableCell>
            <TableCell>{data?.based_on}</TableCell>
            <TableCell>{data?.quantity}</TableCell>
            <TableCell>{data?.factor}</TableCell>
            <TableCell>
              {!isBandEarning(data?.type) && (
                <IconButton
                  disabled={isEditable}
                  onClick={() => {
                    setShowConfirmationDialog(true);
                    setSelectedData(data);
                    setIsOtherEarnings(true);
                    setIsPenalty(false);
                    setIsSummary(false);
                  }}
                >
                  <Delete
                    color={isEditable ? themes.disabledColor : "error"}
                    sx={{ fontSize: 20, cursor: "pointer" }}
                  />
                </IconButton>
              )}
            </TableCell>
          </TableRow>
        )}
      />

      <AttendanceCalendar
        attendanceDayDetails={attendanceDayDetails}
        colorCode={colorCode}
      />

      <SectionHeader>Manual Adjustment History</SectionHeader>
      <Mui.TableContainer
        component={Mui.Paper}
        sx={{ mt: 2, width: "100%", maxHeight: 850 }}
      >
        <Mui.Table>
          <Mui.TableHead
            sx={{
              backgroundColor: themes.primary,
              position: "sticky",
              top: 0,
              zIndex: 10,
              "& .MuiTableCell-root": {
                height: "15px",
                padding: "10px 20px",
                color: themes.headLine,
                fontWeight: "bold",
              },
            }}
          >
            <Mui.TableRow>
              <Mui.TableCell />
              <Mui.TableCell>Adjusted by</Mui.TableCell>
              <Mui.TableCell>Adjusted on</Mui.TableCell>
              <Mui.TableCell>Adjustment</Mui.TableCell>
              <Mui.TableCell>Reason</Mui.TableCell>
            </Mui.TableRow>
          </Mui.TableHead>

          <Mui.TableBody>
            {attendanceDetails?.attendance?.manual_adjustment_history
              ?.length === 0 ? (
              <TableEmptyState
                dataLength={
                  attendanceDetails?.attendance?.manual_adjustment_history
                }
              />
            ) : (
              attendanceDetails?.attendance?.manual_adjustment_history?.map(
                (row, index) => (
                  <React.Fragment key={index}>
                    <Mui.TableRow>
                      <Mui.TableCell>
                        <Mui.IconButton
                          onClick={() =>
                            setExpandedRows(
                              expandedRows === index ? null : index,
                            )
                          }
                        >
                          {expandedRows === index ? (
                            <KeyboardArrowDown
                              style={{ color: themes.headLine }}
                            />
                          ) : (
                            <KeyboardArrowRight
                              style={{ color: themes.headLine }}
                            />
                          )}
                        </Mui.IconButton>
                      </Mui.TableCell>
                      <Mui.TableCell>{row.adjusted_by || "N/A"}</Mui.TableCell>
                      <Mui.TableCell>{row.modifiedat || "N/A"}</Mui.TableCell>
                      <Mui.TableCell>{row.adjustment || "N/A"}</Mui.TableCell>
                      <Mui.TableCell>{row.reason || "N/A"}</Mui.TableCell>
                    </Mui.TableRow>

                    <Mui.TableRow>
                      <Mui.TableCell
                        colSpan={5}
                        sx={{ paddingBottom: 0, paddingTop: 0 }}
                      >
                        <Mui.Collapse
                          in={expandedRows === index}
                          timeout="auto"
                          unmountOnExit
                        >
                          <Mui.Box sx={{ margin: 2 }}>
                            <Mui.Table size="small">
                              <Mui.TableHead
                                sx={{
                                  backgroundColor: "#F7F7F7",
                                  "& .MuiTableCell-root": {
                                    fontWeight: "bold",
                                  },
                                }}
                              >
                                <Mui.TableRow>
                                  <Mui.TableCell>Other Earnings</Mui.TableCell>
                                  <Mui.TableCell>Count</Mui.TableCell>
                                  <Mui.TableCell>Hours</Mui.TableCell>
                                  <Mui.TableCell>Basis</Mui.TableCell>
                                  <Mui.TableCell>Qty</Mui.TableCell>
                                  <Mui.TableCell>Factor</Mui.TableCell>
                                  <Mui.TableCell>Adjusted</Mui.TableCell>
                                </Mui.TableRow>
                              </Mui.TableHead>
                              <Mui.TableBody>
                                {row.other_earnings?.map((earning, idx) => (
                                  <Mui.TableRow key={idx}>
                                    <Mui.TableCell>
                                      {earning.title}
                                    </Mui.TableCell>
                                    <Mui.TableCell>
                                      {earning.count}
                                    </Mui.TableCell>
                                    <Mui.TableCell>
                                      {earning.hours}
                                    </Mui.TableCell>
                                    <Mui.TableCell>
                                      {earning.basis}
                                    </Mui.TableCell>
                                    <Mui.TableCell>{earning.qty}</Mui.TableCell>
                                    <Mui.TableCell>
                                      {earning.factor}
                                    </Mui.TableCell>
                                    <Mui.TableCell sx={{ color: "blue" }}>
                                      {earning.adjusted}
                                    </Mui.TableCell>
                                  </Mui.TableRow>
                                ))}
                              </Mui.TableBody>
                            </Mui.Table>
                          </Mui.Box>
                        </Mui.Collapse>
                      </Mui.TableCell>
                    </Mui.TableRow>
                  </React.Fragment>
                ),
              )
            )}
          </Mui.TableBody>
        </Mui.Table>
      </Mui.TableContainer>
    </Grid>
  );
};
